import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, DollarSign, UserX, Wallet, FileText, Building2, ClipboardCheck, ShieldCheck, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Category → icon + accent. Kept in step with the backend aggregator categories.
const CAT = {
  compensation_approval: { icon: Wallet, color: '#F59E0B', label: 'Compensation' },
  termination_approval: { icon: UserX, color: '#EF4444', label: 'Termination' },
  budget_approval: { icon: DollarSign, color: '#7C3AED', label: 'Budget' },
  financial_approval: { icon: DollarSign, color: '#10B981', label: 'Billing' },
  building_intelligence: { icon: Building2, color: '#38BDF8', label: 'Building Intel' },
  inspection_followup: { icon: ClipboardCheck, color: '#8FC93A', label: 'Follow-up' },
  document_review: { icon: FileText, color: '#C0C5CE', label: 'Documents' },
  _default: { icon: ShieldCheck, color: '#94A3B8', label: 'Action' },
};
const catOf = (k) => CAT[k] || CAT._default;

const whenLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
};

export const NotificationBell = ({ token }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [panelStyle, setPanelStyle] = useState(null);

  const load = () => {
    if (!token) return;
    axios.get(`${API}/api/notifications/center`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data || { count: 0, items: [] }))
      .catch(() => setData({ count: 0, items: [] }));
  };

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [token]);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('touchstart', onDoc); };
  }, []);

  // Viewport-aware placement: anchor the panel under the bell, align its right edge to the
  // bell, then clamp fully inside the viewport with a safe margin so it can never spill past
  // either screen edge regardless of where the bell sits in the header. Uses fixed positioning
  // and caps the height so the list scrolls internally on small screens.
  const place = React.useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(vw - margin * 2, 380);
    let left = r.right - width;
    left = Math.max(margin, Math.min(left, vw - margin - width));
    const top = r.bottom + 8;
    const maxHeight = Math.max(200, vh - top - margin);
    setPanelStyle({ position: 'fixed', top, left, width, maxHeight, zIndex: 50 });
  }, []);

  useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, place]);

  const count = data?.count || 0;
  const items = data?.items || [];

  const openItem = (it) => {
    // Opening a notification navigates to the authorized action — it NEVER resolves it.
    setOpen(false);
    if (it?.link) navigate(it.link);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        ref={btnRef}
        data-testid="notification-bell-btn"
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        aria-label={`Notifications${count ? ` (${count} pending)` : ''}`}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-white/5 text-white outline-none transition-colors hover:bg-white/15 focus-visible:bg-white/15 focus-visible:ring-2 focus-visible:ring-white/40"
      >
        <Bell size={18} />
        {count > 0 && (
          <span data-testid="notification-bell-count"
            className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-[#EF4444] px-1 text-[10px] font-bold leading-[18px] text-white ring-2 ring-[#101418]">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel"
          style={panelStyle || { position: 'fixed', top: -9999, left: -9999 }}
          className="flex flex-col overflow-hidden rounded-2xl border border-white/12 bg-[#0E2A47] shadow-2xl ring-1 ring-black/40">
          <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-white/[0.04] px-4 py-3">
            <div className="flex min-w-0 items-center gap-2 text-sm font-bold text-white">
              <Bell size={15} className="shrink-0 text-[#8FC93A]" /> Notifications
              <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-slate-200" data-testid="notification-panel-count">{count} pending</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="notification-panel-close" className="ml-2 shrink-0 rounded-full p-1 text-slate-300 outline-none hover:bg-white/10 hover:text-white">
              <X size={15} />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {data === null && <div className="px-4 py-6 text-center text-sm text-slate-400">Loading…</div>}
            {data !== null && items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-400" data-testid="notification-empty">
                You're all caught up — no actions awaiting you.
              </div>
            )}
            {items.map((it) => {
              const c = catOf(it.category);
              const Icon = c.icon;
              return (
                <button key={it.id} onClick={() => openItem(it)} data-testid={`notification-item-${it.category}-${it.id}`} data-category={it.category}
                  className="group flex w-full items-start gap-3 border-b border-white/5 px-4 py-3 text-left outline-none transition-colors last:border-b-0 hover:bg-white/[0.06] focus-visible:bg-white/[0.06]">
                  <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${c.color}22`, color: c.color }}>
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{ backgroundColor: c.color, color: '#0E2A47' }}>{it.type}</span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-300">Pending</span>
                    </span>
                    <span className="mt-1 block truncate text-sm font-bold text-white">{it.title}</span>
                    {it.description && <span className="mt-0.5 block line-clamp-2 text-xs text-slate-300">{it.description}</span>}
                    {it.datetime && <span className="mt-1 block text-[11px] text-slate-400">{whenLabel(it.datetime)}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
